import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../utils/api';
import Icon from '../../components/AppIcon';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Button from '../../components/ui/Button';

// Persist rank snapshots so we can compute deltas across page visits
const RANK_SNAPSHOT_KEY = 'prepxai_rank_snapshots';

const loadRankSnapshots = () => {
    try { return JSON.parse(localStorage.getItem(RANK_SNAPSHOT_KEY) || '{}'); } catch { return {}; }
};

const saveRankSnapshots = (snapshots) => {
    try { localStorage.setItem(RANK_SNAPSHOT_KEY, JSON.stringify(snapshots)); } catch { }
};

const getRankDelta = (userId, currentRank, snapshots) => {
    const prev = snapshots[userId];
    if (!prev || prev === currentRank) return null;
    return prev - currentRank; // positive = moved up, negative = moved down
};

const Leaderboard = () => {
    const [activeTab, setActiveTab] = useState('global');
    const [rankings, setRankings] = useState([]);
    const [suggestedFriends, setSuggestedFriends] = useState([]);
    const [friendActivity, setFriendActivity] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [userStanding, setUserStanding] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [rankSnapshots, setRankSnapshots] = useState({});
    const pageSize = 10;

    useEffect(() => {
        setRankSnapshots(loadRankSnapshots());
        fetchRankings();
        fetchSidebarData();
    }, [activeTab]);

    const fetchRankings = async (page = 1) => {
        setLoading(true);
        try {
            const endpoint = `/api/rankings/${activeTab}?page=${page}&limit=${pageSize}`;
            const res = await api.get(endpoint);
            const data = res.data.data || [];
            setRankings(data);
            setTotalPages(res.data.meta?.total_pages || 1);
            setCurrentPage(res.data.meta?.page || 1);

            // Save current rank snapshot for each user
            const prevSnapshots = loadRankSnapshots();
            const newSnapshots = { ...prevSnapshots };
            data.forEach((user, i) => {
                const rank = (page - 1) * pageSize + i + 1;
                const key = `${activeTab}_${user.id}`;
                if (!newSnapshots[key]) newSnapshots[key] = rank; // first visit
                // Update rank after delay so delta is visible
                setTimeout(() => {
                    const next = loadRankSnapshots();
                    next[key] = rank;
                    saveRankSnapshots(next);
                }, 5000);
            });
            setRankSnapshots(newSnapshots);
        } catch (err) {
            console.error('Error fetching rankings:', err);
            setRankings([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchSidebarData = async () => {
        try {
            const [suggestedRes, activityRes, friendsRes] = await Promise.all([
                api.get('/api/friends/suggestions'),
                api.get('/api/friends/activity'),
                api.get('/api/friends')
            ]);
            // Add a status field to suggestions for local UI state
            const suggestions = (suggestedRes.data || []).map(s => ({ ...s, status: 'none' }));
            setSuggestedFriends(suggestions);
            setPendingRequests(friendsRes.data?.pending || []);
            // Assuming the API returns current user's rank/standing info somewhere
            // or we fetch it separately. For now, let's look for it in rankings or a profile call
            const profileRes = await api.get('/api/users/profile');
            setUserStanding(profileRes.data);
        } catch (err) {
            console.error('Error fetching sidebar data:', err);
        }
    };

    const handleAddFriend = async (userId) => {
        try {
            await api.post('/api/friends/request', { friend_id: userId });
            setSuggestedFriends(prev => prev.map(s =>
                s.id === userId ? { ...s, status: 'pending' } : s
            ));
        } catch (err) {
            console.error('Error sending friend request:', err);
        }
    };

    const topThree = rankings.slice(0, 3);
    const otherRankings = rankings.slice(3);

    return (
        <div className="min-h-screen bg-white relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[450px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white pointer-events-none z-0" />
            <NavigationHeader />

            <main className="max-w-7xl mx-auto px-6 pt-24 pb-12 relative z-10">
                <div className="flex flex-col lg:flex-row gap-8">

                    {/* LEFT SIDEBAR */}
                    <aside className="w-full lg:w-72 space-y-6">
                        {/* Your Standing */}
                        <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800 shadow-xl shadow-slate-900/10 relative overflow-hidden group">
                            {/* Decorative background element */}
                            <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:bg-emerald-500/20 transition-all duration-500" />

                            <h3 className="text-[10px] font-black text-slate-400tracking-widest mb-4">Your Standing</h3>
                            <div className="flex items-center justify-between mb-4 relative z-10">
                                <div>
                                    <div className="text-3xl font-black text-white">#{userStanding?.rank || '-'}</div>
                                    <div className="flex items-center gap-1 text-[11px] font-bold text-emerald-400">
                                        <Icon name="TrendingUp" size={12} />
                                        Score: {Math.floor(userStanding?.performance_score || 0)}
                                    </div>
                                </div>
                                <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-emerald-400 shadow-inner group-hover:scale-110 transition-transform duration-300">
                                    <Icon name="Award" size={20} />
                                </div>
                            </div>
                            <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden relative z-10">
                                <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 w-[78%] shadow-[0_0_10px_rgba(16,185,129,0.3)]" />
                            </div>
                        </div>

                        {/* Friend Activity */}
                        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-[10px] font-black text-slate-400tracking-widest">Friend Activity</h3>
                                <span className="text-[8px] px-1.5 py-0.5 bg-red-100 text-red-600 font-black rounded uppercase">Live</span>
                            </div>
                            <div className="space-y-4">
                                {friendActivity.slice(0, 3).map((act, i) => (
                                    <div key={i} className="flex gap-3">
                                        <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 bg-slate-100">
                                            <img
                                                src={act.profile_picture_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${act.full_name}`}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div>
                                            <p className="text-[11px] font-bold text-slate-800 leading-tight">
                                                <span className="font-black">{act.full_name.split(' ')[0]}</span> {act.activity_type === 'solved' ? 'solved' : 'earned'} <span className="text-emerald-600">{act.target_name}</span>
                                            </p>
                                            <p className="text-[9px] text-slate-400 font-bold mt-0.5">2m ago</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Button variant="ghost" className="w-full mt-6 text-[10px] font-black text-slate-400tracking-widest py-2 border border-slate-50 hover:bg-slate-50 hover:text-slate-600 transition-all">View All Activity</Button>
                        </div>

                        {/* Friend Requests */}
                        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm ring-2 ring-emerald-500/10">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-[10px] font-black text-slate-400tracking-widest">Friend Requests</h3>
                                <span className={`text-[9px] px-1.5 py-0.5 ${pendingRequests.length > 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'} font-black rounded uppercase`}>
                                    {pendingRequests.length}
                                </span>
                            </div>

                            {pendingRequests.length > 0 ? (
                                <div className="space-y-4">
                                    {pendingRequests.map((req, i) => (
                                        <div key={i} className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 bg-slate-100 cursor-pointer hover:ring-2 hover:ring-emerald-500 transition-all">
                                                    <Link to={`/users/profile/${req.username}`}>
                                                        <img
                                                            src={req.profile_picture_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${req.full_name}`}
                                                            alt=""
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </Link>
                                                </div>
                                                <div className="min-w-0">
                                                    <Link to={`/users/profile/${req.username}`} className="text-[10px] font-black text-slate-800 truncate hover:text-emerald-600 transition-colors">
                                                        {req.full_name}
                                                    </Link>
                                                    <div className="text-[8px] font-bold text-slate-400 uppercase">Wants to practice</div>
                                                </div>
                                            </div>
                                            <div className="flex gap-1.5 ml-2">
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            await api.post('/api/friends/respond', { user_id: req.user_id, status: 'accepted' });
                                                            fetchSidebarData();
                                                            fetchRankings(currentPage);
                                                        } catch (err) { console.error(err); }
                                                    }}
                                                    className="w-6 h-6 rounded-lg bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 transition-all"
                                                >
                                                    <Icon name="Check" size={12} />
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            await api.post('/api/friends/respond', { user_id: req.user_id, status: 'rejected' });
                                                            fetchSidebarData();
                                                        } catch (err) { console.error(err); }
                                                    }}
                                                    className="w-6 h-6 rounded-lg bg-slate-100 text-slate-400 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-all"
                                                >
                                                    <Icon name="X" size={12} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                                    <p className="text-[10px] font-normal text-slate-500tracking-tight">No pending requests</p>
                                </div>
                            )}
                        </div>

                        {/* Suggested for You */}
                        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                            <h3 className="text-[10px] font-black text-slate-400tracking-widest mb-4">Suggested for You</h3>
                            <div className="space-y-4">
                                {suggestedFriends.map((user, i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-100">
                                                <img
                                                    src={user.profile_picture_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.full_name}`}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div>
                                                <div className="text-[11px] font-black text-slate-800">
                                                    <Link to={`/users/profile/${user.username}`} className="hover:text-emerald-600 transition-colors">{user.full_name}</Link>
                                                </div>
                                                <div className="text-[9px] font-bold text-slate-400">{user.college || '-'}</div>
                                            </div>
                                        </div>
                                        {user.status === 'pending' ? (
                                            <div className="px-2.5 py-1 bg-slate-50 text-slate-400 text-[9px] font-blacktracking-wider rounded-md border border-slate-100 cursor-default">
                                                Pending
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => handleAddFriend(user.id)}
                                                className="px-2.5 py-1 bg-slate-900 text-white text-[9px] font-blacktracking-wider rounded-md hover:bg-emerald-500 transition-all flex items-center gap-1"
                                            >
                                                <Icon name="UserPlus" size={10} />
                                                Add
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <Button variant="ghost" className="w-full mt-6 text-[10px] font-black text-slate-400tracking-widest py-2 border border-slate-50 hover:bg-slate-50 hover:text-slate-600 transition-all">View More</Button>
                        </div>
                    </aside>

                    {/* MAIN CONTENT */}
                    <div className="flex-1 space-y-8">

                        {/* Rankings Header & Tabs */}
                        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-2xl font-black text-slate-900 tracking-tight">Elite Rankings <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-600 text-[10px]font-black rounded-full tracking-wider">Global</span></h1>
                                    <Link
                                        to="/ranking-system"
                                        className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-600"
                                        title="How rankings are calculated"
                                        aria-label="How rankings are calculated"
                                    >
                                        <Icon name="Info" size={15} />
                                    </Link>
                                </div>
                                <p className="mt-1 text-xs font-bold text-slate-400">Global score uses normalized report percentiles across the whole system.</p>
                            </div>
                            <div className="flex p-1 bg-slate-100 rounded-xl">
                                {['global', 'college', 'batch', 'friends'].map((tab) => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`px-4 py-1.5 rounded-lg text-xs font-blacktracking-wider transition-all ${activeTab === tab
                                            ? 'bg-white text-slate-900 shadow-sm'
                                            : 'text-slate-400 hover:text-slate-600'
                                            }`}
                                    >
                                        {tab === 'batch' ? 'Batch' : tab === 'college' ? 'My College' : tab}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Top 3 Podium */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-10 px-4">
                            {/* #2 */}
                            {topThree[1] && (
                                <div className="bg-white p-6 rounded-[24px] border border-slate-100 shadow-sm relative text-center mt-8">
                                    <div className="absolute -top-10 left-1/2 -translate-x-1/2">
                                        <div className="relative">
                                            <div className="w-20 h-20 rounded-full border-4 border-white shadow-xl overflow-hidden bg-slate-100">
                                                <img
                                                    src={topThree[1].profile_picture_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${topThree[1].full_name}`}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-slate-100 rounded-full border-2 border-white flex items-center justify-center text-slate-400">
                                                <Icon name="Award" size={12} />
                                            </div>
                                        </div>
                                    </div>
                                    <span className="absolute top-4 left-4 text-[11px] font-black text-slate-300">#2</span>
                                    <div className="mt-8">
                                        <Link to={`/users/profile/${topThree[1].username}`} className="text-[15px] font-black text-slate-900 hover:text-emerald-600 transition-colors">{topThree[1].full_name}</Link>
                                        <p className="text-[10px] font-bold text-slate-400tracking-widest mt-1">{topThree[1].college || 'University'} • {topThree[1].graduation_year || '2025'}</p>
                                        <div className="mt-4 text-xl font-black text-slate-700">{Math.floor(topThree[1].performance_score).toLocaleString()}</div>
                                    </div>
                                </div>
                            )}

                            {/* #1 */}
                            {topThree[0] && (
                                <div className="bg-white p-8 rounded-[32px] border-2 border-amber-400/20 shadow-xl shadow-amber-500/5 relative text-center transform md:scale-110 z-10">
                                    <div className="absolute -top-12 left-1/2 -translate-x-1/2">
                                        <div className="relative">
                                            <div className="w-24 h-24 rounded-full border-4 border-white shadow-2xl overflow-hidden bg-slate-100 ring-4 ring-amber-400/10">
                                                <img
                                                    src={topThree[0].profile_picture_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${topThree[0].full_name}`}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-amber-400 rounded-full border-2 border-white flex items-center justify-center text-white shadow-lg">
                                                <Icon name="Star" size={16} fill="currentColor" />
                                            </div>
                                        </div>
                                    </div>
                                    <span className="absolute top-6 left-6 text-[13px] font-black text-amber-500">#1</span>
                                    <div className="mt-10">
                                        <Link to={`/users/profile/${topThree[0].username}`} className="text-[17px] font-black text-slate-900 hover:text-emerald-600 transition-colors">{topThree[0].full_name}</Link>
                                        <p className="text-[11px] font-bold text-slate-400tracking-widest mt-1">{topThree[0].college || 'University'} • {topThree[0].graduation_year || '2024'}</p>
                                        <div className="mt-4 text-2xl font-black text-emerald-500">{Math.floor(topThree[0].performance_score).toLocaleString()}</div>
                                    </div>
                                </div>
                            )}

                            {/* #3 */}
                            {topThree[2] && (
                                <div className="bg-white p-6 rounded-[24px] border border-slate-100 shadow-sm relative text-center mt-8">
                                    <div className="absolute -top-10 left-1/2 -translate-x-1/2">
                                        <div className="relative">
                                            <div className="w-20 h-20 rounded-full border-4 border-white shadow-xl overflow-hidden bg-slate-100">
                                                <img
                                                    src={topThree[2].profile_picture_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${topThree[2].full_name}`}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-700/10 rounded-full border-2 border-white flex items-center justify-center text-amber-700">
                                                <Icon name="Award" size={12} />
                                            </div>
                                        </div>
                                    </div>
                                    <span className="absolute top-4 left-4 text-[11px] font-black text-slate-300">#3</span>
                                    <div className="mt-8">
                                        <Link to={`/users/profile/${topThree[2].username}`} className="text-[15px] font-black text-slate-900 hover:text-emerald-600 transition-colors">{topThree[2].full_name}</Link>
                                        <p className="text-[10px] font-bold text-slate-400tracking-widest mt-1">{topThree[2].college || 'University'} • {topThree[2].graduation_year || '2024'}</p>
                                        <div className="mt-4 text-xl font-black text-slate-700">{Math.floor(topThree[2].performance_score).toLocaleString()}</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Rankings Table */}
                        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden mt-10">
                            <table className="w-full text-left">
                                <thead className="bg-slate-50/50 border-b border-slate-100">
                                    <tr>
                                        <th className="px-6 py-4 text-[10px] font-black text-slate-400tracking-widest">Rank</th>
                                        <th className="px-6 py-4 text-[10px] font-black text-slate-400tracking-widest">Competitor</th>
                                        <th className="px-6 py-4 text-[10px] font-black text-slate-400tracking-widest">Institution</th>
                                        <th className="px-6 py-4 text-[10px] font-black text-slate-400tracking-widest text-center">Trend</th>
                                        <th className="px-6 py-4 text-[10px] font-black text-slate-400tracking-widest text-right">Score</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {loading ? (
                                        [1, 2, 3, 4, 5].map(i => (
                                            <tr key={i} className="animate-pulse">
                                                <td colSpan={5} className="px-6 py-4 h-16 bg-slate-50/20" />
                                            </tr>
                                        ))
                                    ) : (
                                        otherRankings.map((user, i) => {
                                            const rank = i + 4;
                                            const isUser = user.username === userStanding?.username;
                                            return (
                                                <tr key={user.id} className={`group hover:bg-slate-50/50 transition-colors ${isUser ? 'bg-emerald-50/50 border-l-4 border-l-emerald-500 shadow-sm' : ''}`}>
                                                    <td className="px-6 py-4">
                                                        <span className={`text-sm font-black ${isUser ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                            #{rank}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center font-black text-slate-500 overflow-hidden">
                                                                <img
                                                                    src={user.profile_picture_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.full_name}`}
                                                                    alt=""
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2">
                                                                    <Link
                                                                        to={`/users/profile/${user.username}`}
                                                                        className="text-sm font-black text-slate-900 hover:text-emerald-600 transition-colors"
                                                                    >
                                                                        {user.full_name}
                                                                        {isUser && <span className="ml-2 text-[8px] px-1.5 py-0.5 bg-emerald-500 text-white rounded font-black tracking-widest">YOU</span>}
                                                                    </Link>
                                                                    {user.is_elite && <span className="px-1.5 py-0.5 bg-slate-900 text-white text-[8px] font-blackrounded tracking-wider">Elite</span>}
                                                                </div>
                                                                <div className="text-[10px] font-bold text-slate-400">@{user.username}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-xs font-bold text-slate-500">{user.college || '-'}</td>
                                                    <td className="px-6 py-4 text-center">
                                                        {(() => {
                                                            const key = `${activeTab}_${user.id}`;
                                                            const delta = getRankDelta(key, rank, rankSnapshots);
                                                            if (delta === null) return <span className="text-slate-300 text-xs font-bold">—</span>;
                                                            if (delta > 0) return (
                                                                <span className="inline-flex items-center gap-0.5 text-emerald-500 font-black text-[11px]">
                                                                    <Icon name="ChevronUp" size={12} />
                                                                    {delta}
                                                                </span>
                                                            );
                                                            if (delta < 0) return (
                                                                <span className="inline-flex items-center gap-0.5 text-rose-500 font-black text-[11px]">
                                                                    <Icon name="ChevronDown" size={12} />
                                                                    {Math.abs(delta)}
                                                                </span>
                                                            );
                                                            return <span className="text-slate-300 text-xs">—</span>;
                                                        })()}
                                                    </td>
                                                    <td className="px-6 py-4 text-right font-black text-slate-900 text-sm">
                                                        {Math.floor(user.performance_score).toLocaleString()}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            <div className="p-6 border-t border-slate-50 flex flex-col items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => fetchRankings(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="w-10 h-10 rounded-xl border border-slate-100 flex items-center justify-center text-slate-400 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-transparent transition-all"
                                    >
                                        <Icon name="ChevronLeft" size={18} />
                                    </button>

                                    {[...Array(totalPages)].map((_, i) => {
                                        const pageNum = i + 1;
                                        // Show first, last, and pages around current
                                        if (
                                            pageNum === 1 ||
                                            pageNum === totalPages ||
                                            (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => fetchRankings(pageNum)}
                                                    className={`w-10 h-10 rounded-xl text-xs font-black transition-all ${currentPage === pageNum
                                                        ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20'
                                                        : 'text-slate-400 hover:bg-slate-50 border border-transparent hover:border-slate-100'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        } else if (
                                            pageNum === currentPage - 2 ||
                                            pageNum === currentPage + 2
                                        ) {
                                            return <span key={pageNum} className="text-slate-300">...</span>;
                                        }
                                        return null;
                                    })}

                                    <button
                                        onClick={() => fetchRankings(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="w-10 h-10 rounded-xl border border-slate-100 flex items-center justify-center text-slate-400 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-transparent transition-all"
                                    >
                                        <Icon name="ChevronRight" size={18} />
                                    </button>
                                </div>
                                <p className="text-[10px] text-slate-400 font-bold">Page {currentPage} of {totalPages} • High Precision Scoring Active</p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Leaderboard;
